import React, {
  lazy, Suspense, useEffect, useState,
} from 'react';
import { flushSync } from 'react-dom';
import CWOConfigProvider from 'helpers/cwoConfigProvider';
import Loading from 'helpers/loading/loading';
import VirtualizedRow from 'components/agri-work-plan-new-design/components/VirtualizedRow';

const Component = lazy(() => import('components/pivot-gps-statistics-items-map'));
const DEFAULT_COLORIZATION = { enabled: false, parameterName: null, colorStops: [] };

// Map is 300px tall; the surrounding placeholder reserves the same space so
// scroll position stays stable when offscreen maps are unmounted.
const RESERVED_HEIGHT = 300;

// Keep ~3 cards above and below the viewport mounted so smooth scrolling
// rarely hits a placeholder. Larger window = less re-init flicker, more
// simultaneous WebGL contexts. 800px ≈ 2-3 cards in either direction —
// well under the browser's ~8-16 context budget for a typical 30-day view.
const VIRTUALIZED_ROOT_MARGIN = '800px 0px';

// Print listeners are installed once at module load and fan out to every
// mounted instance via a subscriber set, so a 30-day page triggers one
// flushSync rather than 30 cascading ones.
const printSubscribers = new Set();
let printListenersInstalled = false;

const installPrintListeners = () => {
  if (printListenersInstalled) return;
  printListenersInstalled = true;

  addEventListener('beforeprint', () => {
    flushSync(() => {
      for (const setForceMount of printSubscribers) setForceMount(true);
    });
  });
  addEventListener('afterprint', () => {
    for (const setForceMount of printSubscribers) setForceMount(false);
  });
};

const usePrintForceMount = () => {
  const [forceMount, setForceMount] = useState(false);

  useEffect(() => {
    installPrintListeners();
    printSubscribers.add(setForceMount);
    return () => {
      printSubscribers.delete(setForceMount);
    };
  }, []);

  return forceMount;
};

const PivotGpsStatisticsItemsMap = (props) => {
  const [colorization, setColorization] = useState(DEFAULT_COLORIZATION);
  // forceVisible keeps <VirtualizedRow> in the same JSX position so already-
  // mounted children preserve identity across the print toggle. flushSync
  // (inside the shared beforeprint listener) commits the state update
  // before the blocking print dialog opens.
  const forceMount = usePrintForceMount();

  return (
    <CWOConfigProvider>
      <Suspense fallback={<Loading />}>
        <VirtualizedRow
          estimatedHeight={RESERVED_HEIGHT}
          rootMargin={VIRTUALIZED_ROOT_MARGIN}
          forceVisible={forceMount}
        >
          <Component
            {...props}
            colorization={colorization}
            onColorizationChange={setColorization}
          />
        </VirtualizedRow>
      </Suspense>
    </CWOConfigProvider>
  );
};

export default PivotGpsStatisticsItemsMap;
